import Decimal from 'decimal.js';
import { CurrencySymbol } from '@libs/types';
import { RegexValidators } from '@libs/validations';
import { getDecimalValue } from './decimalWrapper';
export const getAmountPrefixAndColor = (colors, change, percentage) => {
    const percentageChange = +(percentage || '0');
    const assetChange = +(change || '0');
    let changeColor = colors.text.secondary;
    let prefix = '';
    if (assetChange > 0) {
        changeColor = colors.text.success;
        prefix = '+';
    }
    else if (assetChange < 0) {
        changeColor = colors.text.critical;
        prefix = '-';
    }
    const hasGrown = assetChange > 0;
    const hasShrunk = assetChange < 0;
    const hasNotChanged = assetChange === 0;
    return {
        prefix,
        changeColor,
        hasGrown,
        hasShrunk,
        hasNotChanged,
        absoluteAmount: Math.abs(assetChange),
        absolutePercentage: Math.abs(percentageChange),
    };
};
export const getEstimatedAmount = (val, defaultPrecision = 12, shouldRound = true) => {
    if (val === undefined || val === null)
        return '--';
    const value = getDecimalValue(val || 0);
    let precision = defaultPrecision;
    if (value.cmp(1) === 1) {
        precision = 2;
    }
    else if (value.cmp(0.1) === 1) {
        precision = 4;
    }
    return formatNumber(val, shouldRound ? precision : undefined);
};
export const formatAmountWithSymbol = ({ amount, symbol = CurrencySymbol.USDT, shouldLoad = true, shouldHaveSpace = false, reverse = true, shouldRound = true, roundingPrecision, }) => {
    if (amount === undefined || amount === null) {
        if (shouldLoad) {
            return '--';
        }
        return undefined;
    }
    const space = shouldHaveSpace ? ' ' : '';
    const formattedAmount = getEstimatedAmount(amount || 0, roundingPrecision, shouldRound);
    if (reverse) {
        return formattedAmount + space + symbol;
    }
    return symbol + space + formattedAmount;
};
export const formatNumber = (val, precision = 7) => {
    if (val === undefined)
        return '--';
    const value = getDecimalValue(val || 0).toDecimalPlaces(precision, Decimal.ROUND_DOWN);
    return value.toNumber().toLocaleString('en-US', {
        maximumFractionDigits: precision,
    });
};
export const sum = (firstValue = 0, secondValue = 0) => {
    return getDecimalValue(firstValue).plus(secondValue).toNumber();
};
export const formatCryptoAmount = (amount, price, assetPrecision) => {
    let precision = assetPrecision;
    const amountDecimal = getDecimalValue(amount);
    const priceDecimal = getDecimalValue(price);
    if (amount > 1) {
        const division = amountDecimal.div(priceDecimal);
        if (division.toNumber() > 1) {
            precision = 2;
        }
        else {
            const stringAfterDot = division.toString().replace('0.', '');
            const match = stringAfterDot.match(RegexValidators.ZEROES_AFTER_DOT_IN_DECIMAL);
            precision = match ? match[0].length : 0;
        }
    }
    return formatNumber(amount, precision);
};
export const getEstimatedCryptoAmount = (amount, price, decimalPrecision = 7) => {
    const numAmount = amount?.toString() ? +amount : 0;
    const numPrice = price?.toString() ? +price : 0;
    if (numAmount === 0 || price === 0)
        return '0';
    if (numAmount < 1)
        return formatNumber(numAmount, decimalPrecision);
    const division = numAmount / numPrice;
    if (division >= 1) {
        return formatNumber(numAmount, 2);
    }
    else {
        const divisionZeroes = division.toString().split(',')?.[1] || '0';
        const zeroesLength = (divisionZeroes.match(/^0+/) || [''])[0].length || 0;
        return formatNumber(numAmount, zeroesLength + 1);
    }
};
export const getChangePercentage = (currentAmount, prevAmount) => {
    if (typeof currentAmount === 'undefined' ||
        typeof prevAmount === 'undefined') {
        return 0;
    }
    return ((+currentAmount || 0) * 100) / (+prevAmount || 1) - 100;
};
const THOUSAND = 1000;
const MILLION = THOUSAND ** 2;
const BILLION = THOUSAND ** 3;
const TRILLION = THOUSAND ** 4;
const QUADRILLION = THOUSAND ** 5;
export const formatLargeAmount = (amt, currencySymbol, isZeroVisible) => {
    if ((!amt || amt === '0') && !isZeroVisible)
        return '--';
    if (amt === undefined || amt === null)
        return '';
    const absoluteAmount = Math.abs(+amt);
    const amount = absoluteAmount < 1 ? absoluteAmount : Math.round(absoluteAmount);
    const prefix = `${amt >= 0 ? '' : '-'}${currencySymbol || ''}`;
    if (amount < 1000)
        return prefix + amount.toString();
    else if (amount < MILLION) {
        return prefix + (amount / THOUSAND).toFixed(2) + 'K';
    }
    else if (amount < BILLION) {
        return prefix + (amount / MILLION).toFixed(2) + 'M';
    }
    else if (amount < TRILLION) {
        return prefix + (amount / BILLION).toFixed(2) + 'B';
    }
    else if (amount < QUADRILLION) {
        return prefix + (amount / TRILLION).toFixed(2) + 'T';
    }
    return amount.toString();
};
//# sourceMappingURL=amount.js.map